import { calculateBreakpointPosition } from './ui.video-player.helpers';
import { Breakpoint } from './ui.video-player.types';

export interface BreakpointHighlight {
  startPosition: number;
  endPosition: number;
  width: number;
  startTime: number;
  endTime: number;
}

export interface BreakpointPosition {
  position: number;
  isInHighlight: boolean;
}

/**
 * Calculate breakpoint highlight dimensions for selected breakpoint
 * Single source of truth for highlight calculations
 */
export const calculateBreakpointHighlight = (
  selectedBreakpointId: string | null,
  breakpoints: Breakpoint[],
  duration: number
): BreakpointHighlight | null => {
  if (!selectedBreakpointId || !duration || !breakpoints.length) return null;

  const selectedBreakpoint = breakpoints.find(
    (bp) => bp.id === selectedBreakpointId
  );
  if (!selectedBreakpoint) return null;

  const sortedBreakpoints = breakpoints.toSorted((a, b) => a.time - b.time);
  const currentIndex = sortedBreakpoints.findIndex(
    (bp) => bp.id === selectedBreakpointId
  );
  const nextBreakpoint = sortedBreakpoints[currentIndex + 1];
  const endTime = nextBreakpoint ? nextBreakpoint.time : duration;

  const startPosition = calculateBreakpointPosition(
    selectedBreakpoint.time,
    duration
  );
  const endPosition = calculateBreakpointPosition(endTime, duration);
  const width = endPosition - startPosition;

  return {
    startPosition,
    endPosition,
    width,
    startTime: selectedBreakpoint.time,
    endTime,
  };
};

/**
 * Calculate positions for all breakpoints
 * Memoized position calculations to avoid recalculation
 */
export const calculateBreakpointPositions = (
  breakpoints: Breakpoint[],
  duration: number,
  highlight?: BreakpointHighlight | null
): BreakpointPosition[] => {
  if (!duration || !breakpoints.length) return [];

  return breakpoints.map((breakpoint) => {
    const position = calculateBreakpointPosition(breakpoint.time, duration);
    const isInHighlight = highlight
      ? breakpoint.time >= highlight.startTime &&
        breakpoint.time <= highlight.endTime
      : false;

    return {
      position: Math.min(position, 99),
      isInHighlight,
    };
  });
};
